<script setup>
import { computed, ref, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { uploadFile } from 'dashboard/helper/uploadHelper';
import { checkFileSizeLimit, formatBytes } from 'shared/helpers/FileHelper';
import Button from 'dashboard/components-next/button/Button.vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';

const props = defineProps({
  data: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['update']);

const timeoutUnits = [
  { value: 'seconds', label: 'SECONDS' },
  { value: 'minutes', label: 'MINUTES' },
  { value: 'hours', label: 'HOURS' },
  { value: 'days', label: 'DAYS' },
];

const { t } = useI18n();
const getters = useStoreGetters();
const store = useStore();
const accountId = computed(() => getters.getCurrentAccountId.value);

onMounted(() => {
  store.dispatch('attributes/get');
});

const data = computed(() => props.data);
const items = computed(() => data.value.content_items || []);

// Use a same-origin relative URL for preview/playback so the browser can load
// the media even when FRONTEND_URL points to a different host (e.g. Docker
// internal IP for WAHA webhooks while the SPA is opened on localhost).
const previewUrl = url => {
  if (!url) return url;
  try {
    const parsed = new URL(url, window.location.href);
    if (parsed.origin !== window.location.origin) {
      return parsed.pathname + parsed.search + parsed.hash;
    }
  } catch {
    // ignore malformed URLs
  }
  return url;
};
const label = computed(() => data.value.label || '');

const waitForResponse = ref(Boolean(props.data?.wait_for_response));
const responseTimeoutValue = ref(props.data?.timeout_value || 5);
const responseTimeoutUnit = ref(props.data?.timeout_unit || 'minutes');

// 'existing' = save to existing attribute, 'new' = create new, 'none' = don't save
const saveMode = ref(props.data?.attribute_key ? 'existing' : 'none');
const responseAttributeKey = ref(props.data?.attribute_key || '');
const newAttributeName = ref(props.data?.attribute_display_name || '');
const newAttributeKey = ref(props.data?.attribute_key || '');
const isCreatingAttribute = ref(false);
const attributeCreated = ref(false);

const availableAttributes = computed(() => {
  const contactAttrs = getters['attributes/getContactAttributes'].value || [];
  const conversationAttrs =
    getters['attributes/getConversationAttributes'].value || [];
  return [...contactAttrs, ...conversationAttrs];
});

const selectedAttribute = computed(() =>
  availableAttributes.value.find(
    attr => attr.attributeKey === responseAttributeKey.value
  )
);

const updateKeyFromName = () => {
  if (saveMode.value === 'new') {
    newAttributeKey.value = newAttributeName.value
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_]/g, '');
    attributeCreated.value = false;
  }
};

const canCreateAttribute = computed(
  () =>
    newAttributeName.value.trim() &&
    newAttributeKey.value.trim() &&
    !attributeCreated.value
);

const createAttribute = async () => {
  if (!canCreateAttribute.value) return;
  isCreatingAttribute.value = true;
  try {
    await store.dispatch('attributes/create', {
      attribute_display_name: newAttributeName.value.trim(),
      attribute_key: newAttributeKey.value.trim(),
      attribute_model: 'contact_attribute',
      attribute_display_type: 0,
    });
    attributeCreated.value = true;
    useAlert(t('FLOW_BUILDER.NODE_EDITOR.DATA_COLLECTION.ATTRIBUTE_CREATED'));
  } catch (error) {
    useAlert(
      error?.message ||
        t('FLOW_BUILDER.NODE_EDITOR.DATA_COLLECTION.ATTRIBUTE_CREATE_ERROR')
    );
  } finally {
    isCreatingAttribute.value = false;
  }
};

const updateResponseConfig = () => {
  let attributeKey = null;
  let attributeDisplayName = null;

  if (saveMode.value === 'existing') {
    attributeKey =
      selectedAttribute.value?.attributeKey || responseAttributeKey.value;
    attributeDisplayName =
      selectedAttribute.value?.attributeDisplayName ||
      selectedAttribute.value?.attributeKey ||
      responseAttributeKey.value;
  } else if (saveMode.value === 'new') {
    attributeKey = newAttributeKey.value;
    attributeDisplayName = newAttributeName.value;
  }

  emit('update', {
    wait_for_response: waitForResponse.value,
    attribute_key: attributeKey,
    attribute_display_name: attributeDisplayName,
    timeout_value: responseTimeoutValue.value,
    timeout_unit: responseTimeoutUnit.value,
  });
};

watch(
  [
    waitForResponse,
    saveMode,
    responseAttributeKey,
    newAttributeName,
    newAttributeKey,
    responseTimeoutValue,
    responseTimeoutUnit,
  ],
  updateResponseConfig
);

const updateLabel = value => {
  emit('update', { label: value });
};

const uploadingIndex = ref(null);
const uploadProgress = ref(0);
const draggingIndex = ref(null);
const reorderDragIndex = ref(null);
const reorderDropIndex = ref(null);
const fileInputRefs = ref({});

const MAX_FILE_SIZE_MB = 40;

const lightboxItem = ref(null);
const openLightbox = item => {
  lightboxItem.value = item;
};
const closeLightbox = () => {
  lightboxItem.value = null;
};

const itemTypes = [
  { type: 'text', icon: 'i-lucide-type', label: 'TEXT' },
  { type: 'image', icon: 'i-lucide-image', label: 'IMAGE' },
  { type: 'audio', icon: 'i-lucide-music', label: 'AUDIO' },
  { type: 'video', icon: 'i-lucide-video', label: 'VIDEO' },
  { type: 'file', icon: 'i-lucide-paperclip', label: 'FILE' },
  { type: 'delay', icon: 'i-lucide-timer', label: 'DELAY' },
];

const delayUnits = [
  { value: 'seconds', label: 'SECONDS' },
  { value: 'minutes', label: 'MINUTES' },
  { value: 'hours', label: 'HOURS' },
  { value: 'days', label: 'DAYS' },
];

const addItem = type => {
  const base =
    type === 'delay'
      ? { type, delay_value: 5, delay_unit: 'seconds' }
      : { type, content: '', url: '' };
  const newItems = [...items.value, base];
  emit('update', { content_items: newItems });
};

const moveItem = (index, direction) => {
  const newIndex = index + direction;
  if (newIndex < 0 || newIndex >= items.value.length) return;
  const newItems = [...items.value];
  [newItems[index], newItems[newIndex]] = [newItems[newIndex], newItems[index]];
  emit('update', { content_items: newItems });
};

const onReorderDragStart = (event, index) => {
  reorderDragIndex.value = index;
  event.dataTransfer.effectAllowed = 'move';
  event.dataTransfer.setData('text/plain', `reorder:${index}`);
};

const onReorderDragOver = (event, index) => {
  if (reorderDragIndex.value === null) return;
  event.preventDefault();
  event.dataTransfer.dropEffect = 'move';
  reorderDropIndex.value = index;
};

const onReorderDrop = (event, index) => {
  event.preventDefault();
  if (reorderDragIndex.value === null || reorderDragIndex.value === index) {
    reorderDragIndex.value = null;
    reorderDropIndex.value = null;
    return;
  }
  const newItems = [...items.value];
  const [moved] = newItems.splice(reorderDragIndex.value, 1);
  newItems.splice(index, 0, moved);
  emit('update', { content_items: newItems });
  reorderDragIndex.value = null;
  reorderDropIndex.value = null;
};

const onReorderDragEnd = () => {
  reorderDragIndex.value = null;
  reorderDropIndex.value = null;
};

const removeItem = index => {
  const newItems = items.value.filter((_, i) => i !== index);
  emit('update', { content_items: newItems });
};

const updateItem = (index, field, value) => {
  const newItems = items.value.map((item, i) =>
    i === index ? { ...item, [field]: value } : item
  );
  emit('update', { content_items: newItems });
};

const handleFileUpload = async (file, index) => {
  if (!file) return;

  if (!checkFileSizeLimit(file, MAX_FILE_SIZE_MB)) {
    useAlert(
      t('FLOW_BUILDER.NODE_EDITOR.BLOCK.FILE_SIZE_ERROR_DETAIL', {
        size: formatBytes(file.size),
        limit: `${MAX_FILE_SIZE_MB}MB`,
      })
    );
    return;
  }

  uploadingIndex.value = index;
  uploadProgress.value = 0;
  try {
    const result = await uploadFile(file, accountId.value, percent => {
      uploadProgress.value = percent;
    });
    const newItems = items.value.map((item, i) =>
      i === index
        ? {
            ...item,
            url: result.fileUrl,
            blob_signed_id: result.blobId,
            file_name: file.name,
          }
        : item
    );
    emit('update', { content_items: newItems });
  } catch (error) {
    useAlert(t('FLOW_BUILDER.NODE_EDITOR.BLOCK.UPLOAD_ERROR'));
  } finally {
    uploadingIndex.value = null;
    uploadProgress.value = 0;
  }
};

const onItemDrop = (event, index) => {
  draggingIndex.value = null;
  const files = event.dataTransfer?.files;
  if (!files || files.length === 0) return;
  handleFileUpload(files[0], index);
};

const onItemDragOver = index => {
  draggingIndex.value = index;
};

const onItemDragLeave = () => {
  draggingIndex.value = null;
};

const onItemFileInputChange = (event, index) => {
  const files = event.target.files;
  if (!files || files.length === 0) return;
  handleFileUpload(files[0], index);
  event.target.value = '';
};

const triggerItemFileInput = index => {
  fileInputRefs.value[index]?.click();
};
</script>

<template>
  <div class="flex flex-col gap-3">
    <!-- Node name -->
    <div class="flex flex-col gap-1.5">
      <label class="text-[11px] font-medium text-n-slate-11">
        {{ t('FLOW_BUILDER.NODE_EDITOR.BLOCK.NAME') }}
      </label>
      <input
        :value="label"
        :placeholder="t('FLOW_BUILDER.NODE_EDITOR.BLOCK.NAME_PLACEHOLDER')"
        class="w-full px-3 py-2 text-sm border border-n-weak dark:border-n-strong rounded-lg bg-transparent text-n-slate-12 outline-none focus:border-n-blue-5"
        @input="updateLabel($event.target.value)"
      />
    </div>

    <!-- Add item buttons -->
    <div
      class="flex flex-col gap-1.5 pt-2 border-t border-n-slate-2 dark:border-n-slate-3"
    >
      <label class="text-[11px] font-medium text-n-slate-11">
        {{ t('FLOW_BUILDER.NODE_EDITOR.BLOCK.ADD_ITEM') }}
      </label>
      <div class="flex flex-wrap gap-1.5">
        <button
          v-for="itemType in itemTypes"
          :key="itemType.type"
          class="flex items-center gap-1 px-2 py-1.5 text-xs font-medium rounded-lg border border-n-weak dark:border-n-strong hover:bg-n-slate-2 dark:hover:bg-n-slate-3 transition-colors text-n-slate-12"
          @click="addItem(itemType.type)"
        >
          <span :class="itemType.icon" class="text-xs" />
          {{ t(`FLOW_BUILDER.NODE_EDITOR.BLOCK.TYPES.${itemType.label}`) }}
        </button>
      </div>
    </div>

    <!-- Items list -->
    <div
      v-if="items.length > 0"
      class="flex flex-col gap-2 pt-2 border-t border-n-slate-2 dark:border-n-slate-3"
    >
      <div
        v-for="(item, index) in items"
        :key="index"
        class="flex flex-col gap-2 p-2.5 rounded-lg bg-n-slate-1 dark:bg-n-slate-2"
        @dragover.prevent="onReorderDragOver($event, index)"
        @drop.prevent="onReorderDrop($event, index)"
      >
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-1.5">
            <span
              class="i-lucide-grip-vertical text-xs text-n-slate-11 cursor-grab active:cursor-grabbing"
              draggable="true"
              @dragstart="onReorderDragStart($event, index)"
              @dragend="onReorderDragEnd"
            />
            <span class="text-[11px] font-semibold text-n-slate-12">
              {{
                t(
                  `FLOW_BUILDER.NODE_EDITOR.BLOCK.TYPES.${item.type.toUpperCase()}`
                )
              }}
            </span>
          </div>
          <div class="flex items-center gap-0.5">
            <Button
              variant="ghost"
              size="xs"
              icon="i-lucide-chevron-up"
              :disabled="index === 0"
              @click="moveItem(index, -1)"
            />
            <Button
              variant="ghost"
              size="xs"
              icon="i-lucide-chevron-down"
              :disabled="index === items.length - 1"
              @click="moveItem(index, 1)"
            />
            <Button
              variant="ghost"
              size="xs"
              color="ruby"
              icon="i-lucide-trash-2"
              @click="removeItem(index)"
            />
          </div>
        </div>

        <!-- Text input -->
        <WootMessageEditor
          v-if="item.type === 'text'"
          :model-value="item.content || ''"
          :placeholder="t('FLOW_BUILDER.NODE_EDITOR.BLOCK.TEXT_PLACEHOLDER')"
          enable-variables
          enable-suggestions
          :enable-canned-responses="false"
          :enable-captain-tools="false"
          :enable-macros="false"
          class="[&_.ProseMirror-menubar]:hidden px-3 py-1 bg-n-alpha-1 rounded-lg outline outline-1 outline-n-weak dark:outline-n-strong"
          @update:model-value="updateItem(index, 'content', $event)"
        />

        <!-- Delay input -->
        <div v-else-if="item.type === 'delay'" class="flex items-center gap-2">
          <input
            :value="item.delay_value"
            type="number"
            min="1"
            class="w-20 px-2.5 py-1.5 text-xs border border-n-weak dark:border-n-strong rounded-lg bg-transparent text-n-slate-12 outline-none focus:border-n-blue-5"
            @input="
              updateItem(
                index,
                'delay_value',
                parseInt($event.target.value) || 1
              )
            "
          />
          <select
            :value="item.delay_unit"
            class="px-2.5 py-1.5 text-xs border border-n-weak dark:border-n-strong rounded-lg bg-transparent text-n-slate-12 outline-none focus:border-n-blue-5"
            @change="updateItem(index, 'delay_unit', $event.target.value)"
          >
            <option
              v-for="unit in delayUnits"
              :key="unit.value"
              :value="unit.value"
            >
              {{
                t(`FLOW_BUILDER.NODE_EDITOR.BLOCK.DELAY_UNITS.${unit.label}`)
              }}
            </option>
          </select>
          <span class="i-lucide-timer text-xs text-n-slate-11" />
        </div>

        <!-- Media item: drag-and-drop upload + caption -->
        <div v-else class="flex flex-col gap-2">
          <!-- Image preview with caption overlay -->
          <div
            v-if="item.type === 'image' && item.url"
            class="relative rounded-lg overflow-hidden bg-n-slate-2 dark:bg-n-slate-3 group cursor-pointer"
            @click="openLightbox(item)"
          >
            <img
              :src="previewUrl(item.url)"
              :alt="item.caption || item.file_name || ''"
              class="w-full max-h-32 object-cover"
            />
            <div
              class="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center"
            >
              <span
                class="i-lucide-maximize-2 text-white opacity-0 group-hover:opacity-100 transition-opacity text-lg"
              />
            </div>
            <p
              v-if="item.caption"
              class="absolute bottom-0 left-0 right-0 px-2 py-1 text-[11px] text-white bg-black/50 truncate"
            >
              {{ item.caption }}
            </p>
          </div>

          <!-- Video preview with play button -->
          <div
            v-if="item.type === 'video' && item.url"
            class="relative rounded-lg overflow-hidden bg-n-slate-2 dark:bg-n-slate-3 group cursor-pointer"
            @click="openLightbox(item)"
          >
            <video
              :src="previewUrl(item.url)"
              class="w-full max-h-32 object-cover"
              preload="metadata"
              muted
              playsinline
            />
            <div
              class="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center"
            >
              <span
                class="i-lucide-play-circle text-white opacity-0 group-hover:opacity-100 transition-opacity text-2xl"
              />
            </div>
            <p
              v-if="item.caption"
              class="absolute bottom-0 left-0 right-0 px-2 py-1 text-[11px] text-white bg-black/50 truncate"
            >
              {{ item.caption }}
            </p>
          </div>

          <!-- Audio preview with play button -->
          <div
            v-if="item.type === 'audio' && item.url"
            class="flex items-center gap-2 px-2.5 py-2 rounded-lg bg-n-slate-2 dark:bg-n-slate-3 cursor-pointer"
            @click="openLightbox(item)"
          >
            <span class="i-lucide-music text-sm text-n-slate-11" />
            <span class="text-xs text-n-slate-11 truncate flex-1">
              {{
                item.file_name || t('FLOW_BUILDER.NODE_EDITOR.BLOCK.UPLOADED')
              }}
            </span>
            <span class="i-lucide-play text-sm text-n-blue-5" />
          </div>

          <!-- File info badge for non-media uploads -->
          <div
            v-if="item.blob_signed_id && item.type === 'file'"
            class="flex items-center gap-2 px-2.5 py-2 rounded-lg bg-n-slate-2 dark:bg-n-slate-3"
          >
            <span class="i-lucide-paperclip text-xs text-n-slate-11" />
            <span class="text-xs text-n-slate-11 truncate">
              {{
                item.file_name || t('FLOW_BUILDER.NODE_EDITOR.BLOCK.UPLOADED')
              }}
            </span>
          </div>

          <!-- Drag and drop zone inside each media item -->
          <div
            v-if="!item.url"
            class="rounded-lg border-2 border-dashed p-3 text-center transition-colors cursor-pointer"
            :class="
              draggingIndex === index
                ? 'border-n-blue-5 bg-n-blue-1 dark:bg-n-blue-2'
                : 'border-n-weak dark:border-n-strong hover:border-n-blue-5'
            "
            @drop.prevent.stop="onItemDrop($event, index)"
            @dragover.prevent.stop="onItemDragOver(index)"
            @dragleave.prevent="onItemDragLeave"
            @click="triggerItemFileInput(index)"
          >
            <input
              :ref="
                el => {
                  if (el) fileInputRefs[index] = el;
                }
              "
              type="file"
              class="hidden"
              @change="onItemFileInputChange($event, index)"
            />
            <div
              v-if="uploadingIndex === index"
              class="flex flex-col items-center gap-2"
            >
              <div class="flex items-center gap-2">
                <span
                  class="i-lucide-loader-circle text-base animate-spin text-n-blue-5"
                />
                <span class="text-[11px] text-n-slate-11">
                  {{ t('FLOW_BUILDER.NODE_EDITOR.BLOCK.UPLOADING') }}
                  {{ uploadProgress }}%
                </span>
              </div>
              <div
                class="w-full max-w-[160px] h-1.5 rounded-full bg-n-slate-3 dark:bg-n-slate-4 overflow-hidden"
              >
                <div
                  class="h-full bg-n-blue-5 rounded-full transition-all duration-200"
                  :style="{ width: `${uploadProgress}%` }"
                />
              </div>
            </div>
            <div v-else class="flex flex-col items-center gap-1">
              <span class="i-lucide-upload-cloud text-base text-n-slate-11" />
              <span class="text-[11px] text-n-slate-11">
                {{ t('FLOW_BUILDER.NODE_EDITOR.BLOCK.DROP_ZONE') }}
              </span>
            </div>
          </div>

          <!-- Replace / View buttons when file already uploaded -->
          <div v-else class="flex items-center gap-2">
            <button
              v-if="['image', 'video', 'audio'].includes(item.type)"
              class="flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-n-weak dark:border-n-strong hover:bg-n-slate-2 dark:hover:bg-n-slate-3 transition-colors text-n-slate-12"
              @click="openLightbox(item)"
            >
              <span class="i-lucide-eye text-sm" />
              {{ t('FLOW_BUILDER.NODE_EDITOR.BLOCK.PREVIEW') }}
            </button>
            <button
              class="flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-n-weak dark:border-n-strong hover:bg-n-slate-2 dark:hover:bg-n-slate-3 transition-colors text-n-slate-12"
              @click="triggerItemFileInput(index)"
            >
              <input
                :ref="
                  el => {
                    if (el) fileInputRefs[index] = el;
                  }
                "
                type="file"
                class="hidden"
                @change="onItemFileInputChange($event, index)"
              />
              <span class="i-lucide-refresh-cw text-sm" />
              {{ t('FLOW_BUILDER.NODE_EDITOR.BLOCK.REPLACE_FILE') }}
            </button>
          </div>

          <!-- Caption input (sent as text alongside the media) -->
          <textarea
            :value="item.caption || ''"
            :placeholder="
              t('FLOW_BUILDER.NODE_EDITOR.BLOCK.CAPTION_PLACEHOLDER')
            "
            rows="2"
            class="w-full px-3 py-2 text-sm border border-n-weak dark:border-n-strong rounded-lg bg-transparent text-n-slate-12 outline-none focus:border-n-blue-5 resize-none"
            @input="updateItem(index, 'caption', $event.target.value)"
          />
        </div>
      </div>
    </div>

    <p v-else class="text-xs text-n-slate-11 text-center py-6">
      {{ t('FLOW_BUILDER.NODE_EDITOR.BLOCK.EMPTY') }}
    </p>

    <!-- Wait for response config -->
    <div
      class="flex flex-col gap-3 pt-2 border-t border-n-slate-2 dark:border-n-slate-3"
    >
      <label
        class="flex items-center gap-2 text-xs font-medium text-n-slate-11 cursor-pointer"
      >
        <input
          v-model="waitForResponse"
          type="checkbox"
          class="accent-n-blue-5"
        />
        {{ t('FLOW_BUILDER.NODE_EDITOR.BLOCK.WAIT_FOR_RESPONSE') }}
      </label>

      <div v-if="waitForResponse" class="flex flex-col gap-3 pl-6">
        <div class="flex flex-col gap-2">
          <div class="flex items-center gap-2">
            <input
              id="blockNoSave"
              v-model="saveMode"
              type="radio"
              value="none"
              class="accent-n-blue-5"
            />
            <label
              for="blockNoSave"
              class="text-xs font-medium text-n-slate-11"
            >
              {{ t('FLOW_BUILDER.NODE_EDITOR.BLOCK.DONT_SAVE_RESPONSE') }}
            </label>
          </div>

          <div class="flex items-center gap-2">
            <input
              id="blockExistingAttr"
              v-model="saveMode"
              type="radio"
              value="existing"
              class="accent-n-blue-5"
            />
            <label
              for="blockExistingAttr"
              class="text-xs font-medium text-n-slate-11"
            >
              {{
                t(
                  'FLOW_BUILDER.NODE_EDITOR.DATA_COLLECTION.USE_EXISTING_ATTRIBUTE'
                )
              }}
            </label>
          </div>
          <select
            v-if="saveMode === 'existing'"
            v-model="responseAttributeKey"
            class="w-full px-3 py-2 text-sm border border-n-weak dark:border-n-strong rounded-lg bg-transparent text-n-slate-12 outline-none focus:border-n-blue-5"
          >
            <option value="">
              {{ t('FLOW_BUILDER.NODE_EDITOR.BLOCK.SELECT_ATTRIBUTE') }}
            </option>
            <option
              v-for="attr in availableAttributes"
              :key="attr.attributeKey"
              :value="attr.attributeKey"
            >
              {{ attr.attributeDisplayName }}
            </option>
          </select>

          <div class="flex items-center gap-2 mt-1">
            <input
              id="blockNewAttr"
              v-model="saveMode"
              type="radio"
              value="new"
              class="accent-n-blue-5"
            />
            <label
              for="blockNewAttr"
              class="text-xs font-medium text-n-slate-11"
            >
              {{
                t('FLOW_BUILDER.NODE_EDITOR.DATA_COLLECTION.CREATE_ATTRIBUTE')
              }}
            </label>
          </div>
          <div v-if="saveMode === 'new'" class="flex flex-col gap-1.5">
            <input
              v-model="newAttributeName"
              type="text"
              :placeholder="
                t(
                  'FLOW_BUILDER.NODE_EDITOR.DATA_COLLECTION.ATTRIBUTE_NAME_PLACEHOLDER'
                )
              "
              class="w-full px-3 py-2 text-sm border border-n-weak dark:border-n-strong rounded-lg bg-transparent text-n-slate-12 outline-none focus:border-n-blue-5"
              @input="updateKeyFromName"
            />
            <input
              v-model="newAttributeKey"
              type="text"
              :placeholder="
                t(
                  'FLOW_BUILDER.NODE_EDITOR.DATA_COLLECTION.ATTRIBUTE_KEY_PLACEHOLDER'
                )
              "
              class="w-full px-3 py-2 text-sm border border-n-weak dark:border-n-strong rounded-lg bg-transparent text-n-slate-12 outline-none focus:border-n-blue-5"
            />
            <div
              v-if="attributeCreated"
              class="flex items-center gap-1.5 text-xs text-n-teal-11 mt-1"
            >
              <span class="i-lucide-check-circle w-4 h-4" />
              {{
                t('FLOW_BUILDER.NODE_EDITOR.DATA_COLLECTION.ATTRIBUTE_CREATED')
              }}
            </div>
            <button
              v-else
              type="button"
              :disabled="!canCreateAttribute || isCreatingAttribute"
              class="flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-n-blue-5 text-white hover:bg-n-blue-6 disabled:opacity-50 disabled:cursor-not-allowed transition-colors mt-1"
              @click="createAttribute"
            >
              <span
                v-if="isCreatingAttribute"
                class="i-lucide-loader-2 w-4 h-4 animate-spin"
              />
              <span v-else class="i-lucide-plus w-4 h-4" />
              {{ t('FLOW_BUILDER.NODE_EDITOR.DATA_COLLECTION.CREATE_BUTTON') }}
            </button>
          </div>
        </div>

        <div class="flex flex-col gap-1.5">
          <label class="text-xs font-medium text-n-slate-11">
            {{ t('FLOW_BUILDER.NODE_EDITOR.BLOCK.RESPONSE_TIMEOUT') }}
          </label>
          <div class="flex items-center gap-2">
            <input
              v-model.number="responseTimeoutValue"
              type="number"
              min="1"
              class="flex-1 px-3 py-2 text-sm border border-n-weak dark:border-n-strong rounded-lg bg-transparent text-n-slate-12 outline-none focus:border-n-blue-5"
            />
            <select
              v-model="responseTimeoutUnit"
              class="flex-1 px-3 py-2 text-sm border border-n-weak dark:border-n-strong rounded-lg bg-transparent text-n-slate-12 outline-none focus:border-n-blue-5"
            >
              <option
                v-for="unit in timeoutUnits"
                :key="unit.value"
                :value="unit.value"
              >
                {{
                  t(`FLOW_BUILDER.NODE_EDITOR.BLOCK.DELAY_UNITS.${unit.label}`)
                }}
              </option>
            </select>
          </div>
        </div>

        <div
          class="flex items-start gap-2 p-3 rounded-lg bg-n-amber-2/50 text-n-amber-11 text-xs"
        >
          <span class="i-lucide-info w-4 h-4 shrink-0" />
          <span>
            {{ t('FLOW_BUILDER.NODE_EDITOR.BLOCK.RESPONSE_NOTICE') }}
          </span>
        </div>
      </div>
    </div>

    <!-- Lightbox modal for image/video/audio preview -->
    <Teleport to="body">
      <div
        v-if="lightboxItem"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
        @click.self="closeLightbox"
      >
        <button
          class="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
          @click="closeLightbox"
        >
          <span class="i-lucide-x text-lg" />
        </button>
        <div class="max-w-4xl max-h-[80vh] flex flex-col items-center gap-3">
          <!-- Image preview -->
          <img
            v-if="lightboxItem.type === 'image'"
            :src="previewUrl(lightboxItem.url)"
            :alt="lightboxItem.caption || lightboxItem.file_name || ''"
            class="max-w-full max-h-[70vh] rounded-lg object-contain"
          />
          <!-- Video preview -->
          <video
            v-else-if="lightboxItem.type === 'video'"
            :src="previewUrl(lightboxItem.url)"
            controls
            crossorigin="anonymous"
            autoplay
            class="max-w-full max-h-[70vh] rounded-lg"
          />
          <!-- Audio preview -->
          <div
            v-else-if="lightboxItem.type === 'audio'"
            class="flex flex-col items-center gap-3 p-8"
          >
            <span class="i-lucide-music text-4xl text-white/60" />
            <audio
              :src="previewUrl(lightboxItem.url)"
              controls
              autoplay
              class="w-96"
            />
          </div>
          <!-- Caption / file name -->
          <p
            v-if="lightboxItem.caption || lightboxItem.file_name"
            class="text-sm text-white/80 text-center"
          >
            {{ lightboxItem.caption || lightboxItem.file_name }}
          </p>
        </div>
      </div>
    </Teleport>
  </div>
</template>
